import clsx from 'clsx'
import format from 'date-fns/format'
import { Grid, Icon, Popover, Tooltip } from 'gu-ui-kit'
import { useWalletsActions } from 'modules/Finance/Wallets/useWalletsActions'
import PropTypes from 'prop-types'
import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'
import { formatToString } from 'utils/formatMoney'
import { useWallets } from 'modules/Finance/Wallets/useWallets'
import Price from '../Price'
import StatusLabel from '../StatusLabel'
import styles from 'modules/Finance/Wallets/subcomponents/WalletsSwitcher/WalletsSwitcher.module.scss'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { ROUTES } from 'constants/routes'

const WalletsSwitcher = ({ className = '' }) => {
  const pathGen = useGeneratePathI18n()

  const { t } = useTranslation(['wallets'])
  const listRef = useRef()
  const { bindTrigger, bindPopover, usePopupState } = Popover.usePopover()
  const popoverState = usePopupState({
    variant: 'popover',
    id: 'wallets-switcher-popover',
  })

  const {
    wallets: { data: wallets, loading },
    activeWalletID,
    activeWalletCurrency,
  } = useWallets()

  const { setActiveWalletID } = useWalletsActions()

  return (
    <>
      {loading ? null : (
        <nav
          ref={listRef}
          className={clsx(styles['wallets-switcher'], className)}
        >
          {activeWalletID && (
            <span
              className={styles['wallets-switcher__trigger']}
              {...bindTrigger(popoverState)}
            >
              <span
                className={clsx(
                  styles['wallets-switcher__trigger-icon'],
                  styles[
                    'wallets-switcher__trigger-icon_' +
                      activeWalletCurrency.toLowerCase()
                  ],
                )}
              >
                <Icon
                  size="s"
                  color="basic-primary-white"
                  name={activeWalletCurrency.toLowerCase()}
                />
              </span>
              <span className={styles['wallets-switcher__trigger-text']}>
                {t('card.all')}
              </span>
              <span
                className={clsx(
                  styles['wallets-switcher__trigger-after-icon'],
                  {
                    [styles['wallets-switcher__trigger-after-icon_opened']]:
                      popoverState.isOpen,
                  },
                )}
              >
                <Icon
                  size="m"
                  color="basic-arrow1-down-primary"
                  name="arrow1-down"
                />
              </span>
            </span>
          )}
          <div
            className={clsx(styles['wallets-switcher__bg'], {
              [styles['wallets-switcher__bg_shown']]: popoverState.isOpen,
            })}
          />
          <Popover
            evaluation={0}
            disablePortal={true}
            hideBackdrop={true}
            {...bindPopover(popoverState)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 10,
              horizontal: 'left',
            }}
          >
            <div
              className={clsx(styles['wallets-switcher__list'], {
                [styles['wallets-switcher__list_shown']]: popoverState.isOpen,
              })}
            >
              <Grid
                className={styles['wallets-switcher__items-wrapper']}
                direction={'column'}
                wrap={false}
              >
                {wallets.map((wallet) => {
                  const {
                    id,
                    currency,
                    balance,
                    financial_profile: {
                      custom_name,
                      financial_profile_type: { type = null },
                    } = {},
                    promisedPayment = null,
                  } = wallet
                  return (
                    <div
                      key={id}
                      className={clsx(
                        styles['wallets-switcher__item'],
                        {
                          [styles['wallets-switcher__item_expired']]:
                            promisedPayment && promisedPayment.is_expired,
                        },
                        {
                          [styles['wallets-switcher__item_taken']]:
                            promisedPayment && !promisedPayment.is_expired,
                        },
                        {
                          [styles['wallets-switcher__item_active']]:
                            activeWalletID === id,
                        },
                      )}
                    >
                      <NavLink
                        to={pathGen(ROUTES.FINANCE_WALLET, {
                          walletID: id,
                        })}
                        className={styles['wallets-switcher__link']}
                        onClick={() => {
                          popoverState.close()
                          setActiveWalletID(id)
                        }}
                      >
                        <div
                          className={clsx(
                            styles['wallets-switcher__icon'],
                            styles[
                              `wallets-switcher__icon_${currency.toLowerCase()}`
                            ],
                          )}
                        >
                          <Icon
                            size="s"
                            color="basic-primary-white"
                            name={currency.toLowerCase()}
                          />
                        </div>
                        <div className={styles['wallets-switcher__context']}>
                          <div className={styles['wallets-switcher__cost']}>
                            <Price
                              className={
                                styles['wallets-switcher__cost-number']
                              }
                              number={balance}
                              currency={currency}
                            />
                            {promisedPayment && !promisedPayment.is_expired && (
                              <div
                                className={
                                  styles['wallets-switcher__status-icon']
                                }
                              >
                                <Tooltip
                                  title={`${t(
                                    'nav.payment.taken',
                                  )} ${formatToString(
                                    promisedPayment.repayment_amount,
                                    currency,
                                  )} ${t('nav.by')}  ${format(
                                    promisedPayment.repayment_date * 1000,
                                    t('date_format.compact'),
                                  )}`}
                                  arrow
                                  placement="right"
                                >
                                  <Icon
                                    name="status pause"
                                    width={16}
                                    height={16}
                                  />
                                </Tooltip>
                              </div>
                            )}
                            {promisedPayment && promisedPayment.is_expired && (
                              <div
                                className={
                                  styles['wallets-switcher__status-icon']
                                }
                              >
                                <Tooltip
                                  title={`${t(
                                    'nav.payment.expired',
                                  )} ${formatToString(
                                    promisedPayment.repayment_amount,
                                    currency,
                                  )}`}
                                  arrow
                                  placement="right"
                                >
                                  <Icon
                                    name="status warning"
                                    color="secondary-46"
                                    width={16}
                                    height={16}
                                  />
                                </Tooltip>
                              </div>
                            )}
                          </div>
                          {promisedPayment && !promisedPayment.is_expired && (
                            <StatusLabel
                              className={styles['wallets-switcher__label']}
                            >
                              {t('types.taken')}
                            </StatusLabel>
                          )}
                          {promisedPayment && promisedPayment.is_expired && (
                            <StatusLabel
                              className={styles['wallets-switcher__label']}
                            >
                              {t('types.expired')}
                            </StatusLabel>
                          )}
                          <div className={styles['wallets-switcher__summary']}>
                            {custom_name ?? t(`wallets::types.${type}`)}
                          </div>
                        </div>
                      </NavLink>
                    </div>
                  )
                })}
              </Grid>
            </div>
          </Popover>
        </nav>
      )}
    </>
  )
}

export default WalletsSwitcher

WalletsSwitcher.propTypes = {
  className: PropTypes.string,
}
